import { CreateNewOrderFormType, CreateNewOrderPayload } from 'api';
import { CUSTOMIZE_PACKAGE_ID } from 'modules/v2/order/neworder/CreateOrderStep2';

function splitFullName(fullName: string | undefined): { firstName: string; lastName?: string } {
  const trimmed = fullName?.trim() ?? '';
  if (!trimmed) return { firstName: '', lastName: undefined };
  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? '';
  const lastName = parts.slice(1).join(' ').trim();
  return { firstName, lastName: lastName || undefined };
}

export const transformOrderCreationDataV2 = (data: CreateNewOrderFormType): CreateNewOrderPayload => {
  const { basicPackage, bundlePackagesProduct, singleProducts } = data;

  const transformBundlePackages = Object.values(bundlePackagesProduct || {}).flatMap(items => items || []);
  const transformSingleProducts = Object.values(singleProducts || {}).flatMap(items => items || []);

  const bundleIds = [
    ...(basicPackage?.id === CUSTOMIZE_PACKAGE_ID ? [] : [basicPackage]),
    ...transformBundlePackages,
  ].map(bundle => bundle?.id ?? '');

  const addons = transformSingleProducts
    .filter(product => (product.quantity ?? 0) > 0)
    .map(product => ({
      productId: product.id,
      quantity: product.quantity ?? 0,
    }));

  const bookingInfo = {
    bookingRequested: true,
    address: {
      street: data?.address?.street,
      buildingNumber: data?.address?.houseNumber?.toString(),
      buildingFloor: data?.address?.floor?.toString(),
      buildingDoor: data?.address?.appartmentNumber?.toString(),
      cityArea: data?.address?.area,
      city: data?.address?.city,
      zip: data?.address?.postNumber?.toString(),
    },
    pickUpKeyRequested: data?.keyPickup,
    pickUpKeyAddress: {
      street: data.pickUpKeyAddress?.street,
      buildingNumber: data.pickUpKeyAddress?.houseNumber?.toString(),
      buildingFloor: data.pickUpKeyAddress?.floor?.toString(),
      buildingDoor: data.pickUpKeyAddress?.appartmentNumber?.toString(),
      cityArea: data.pickUpKeyAddress?.area,
      city: data.pickUpKeyAddress?.city,
      zip: data.pickUpKeyAddress?.postNumber?.toString(),
    },
    commentForPhotographers: data?.photographerComment,
    contactPersons: data?.contactPersons.map(contactPerson => ({
      contactType: contactPerson.type,
      firstName: contactPerson.name,
      lastName: '',
      phone: contactPerson.phone,
      alternativePhone: contactPerson.phoneAlt,
      email: contactPerson.email,
    })),
    salesStylingRequested: data?.hasExternalStylist,
  };

  const propertyInfo = {
    basementArea: data?.property?.basementArea ?? 0,
    registeredArea: data?.property?.livingArea ?? 0,
    totalStoreys: data?.property?.floors ?? 0,
    propertyType: data?.property?.type ?? '',
  };

  const invoicing = {
    endUserInvoicing: data?.shouldSendEndUserInvoice
      ? {
          requested: true,
          address: {
            street: data.endUserInvoice?.address?.street,
            buildingNumber: data.endUserInvoice?.address?.houseNumber?.toString(),
            buildingFloor: data.endUserInvoice?.address?.floor?.toString(),
            buildingDoor: data.endUserInvoice?.address?.appartmentNumber?.toString(),
            cityArea: data.endUserInvoice?.address?.area,
            city: data.endUserInvoice?.address?.city,
            zip: data.endUserInvoice?.address?.postNumber?.toString(),
          },
          ...splitFullName(data?.endUserInvoice?.name),
          email: data?.endUserInvoice?.email,
          phone: data?.endUserInvoice?.phone,
        }
      : undefined,
    businessInvoicing: data?.shouldSendElectronicInvoice
      ? {
          requested: true,
          address: {
            street: data.electronicInvoice?.address?.street,
            buildingNumber: data.electronicInvoice?.address?.houseNumber?.toString(),
            buildingFloor: data.electronicInvoice?.address?.floor?.toString(),
            buildingDoor: data.electronicInvoice?.address?.appartmentNumber?.toString(),
            cityArea: data.electronicInvoice?.address?.area,
            city: data.electronicInvoice?.address?.city,
            zip: data.electronicInvoice?.address?.postNumber?.toString(),
          },
          firstName: data?.electronicInvoice?.name,
          email: data?.electronicInvoice?.email,
          phone: data?.electronicInvoice?.phone,
          vat: data?.electronicInvoice?.cvr,
          ean: data?.electronicInvoice.ean,
          comment: data?.electronicInvoice.comment ?? '',
        }
      : undefined,
  };

  const orderContacts = data.contactPersons?.length
    ? data.contactPersons?.map(({ name, phone, email, type }) => ({
        name,
        phone,
        email,
        contactType: type,
      }))
    : [];

  return {
    address: {
      ...data.address,
      postNumber: data.address?.postNumber?.toString(),
    },
    orderContacts: orderContacts,
    branchId: data.branchId,
    reference: data.case?.reference,
    bookingInfo: bookingInfo,
    propertyInfo: propertyInfo,
    responsibleEmail: data?.responsibleEmail,
    package: data?.packages?.id,
    productChoices: data?.productChoices,
    invoicing: invoicing,
    watcherEmails: data?.watcherEmails ?? [],
    orderId: data?.orderId,
    bundleIds: bundleIds,
    addons: addons,
    type: data?.packageType,
  };
};
